import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { client, OutboundTaskTypeKey, OutboundTaskType, TaskComment, TaskEvent } from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields } from '../../types';
import { TaskContext } from '../../contexts';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { AttachedFile } from '../../Files';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import { ActionCard } from '../../../ActionCard';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { sponsoredTaskCopyWithPaNotice } from '../../../businessRelationshipNotices/copy';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

/**
 * Dedicated task view for PA expiring (PA_EXPIRING).
 */
const PA_EXPIRING_OPTIONS = [
  { label: 'I will renew', value: 'will_renew' as const },
  { label: 'I have already renewed', value: 'already_renewed' as const },
];

const PaExpiringTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [showNewFollowupTaskModal, setShowNewFollowupTaskModal] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>(PA_EXPIRING_OPTIONS[0].value);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const isCompleted = task.status === 'completed';
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const handleTaskResponse = useMutation({
    mutationFn: async (response: string) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "${response}" for task ID: ${taskId}`);
        return;
      }

      const formData = new FormData();
      formData.append('task_response', response);
      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const initialComment = task.timeline?.[0] as TaskComment | undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      completionEvent = lastTimelineItem?.item_type === 'event' ? (lastTimelineItem as TaskEvent) : undefined;
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for PA Expiring task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  const handleSubmit = () => {
    handleTaskResponse.mutate(selectedValue);
  };

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopyWithPaNotice,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className={`mb-6 flex flex-col gap-4 ${isCompleted ? 'border-b-grayscale-100 pb-4 border-b-solid' : ''}`}>
          <TaskInstructionSummary>Please indicate whether you will renew this prescription:</TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <>
              <Typography styledAs="bodyExtraSmallDMSans">
                <RenderHtmlString>{initialComment.message}</RenderHtmlString>
              </Typography>
              {initialComment.attachment_document_ids && initialComment.attachment_document_ids.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  {initialComment.attachment_document_ids.map((fileId) => (
                    <AttachedFile key={fileId} fileId={fileId} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action card - only show when task is not completed */}
        {!isCompleted && (
          <ActionCard>
            <RadioBlockGroup $variant="outlined">
              {PA_EXPIRING_OPTIONS.map((option) => (
                <RadioOptionWrapper
                  key={option.value}
                  id={option.value}
                  name="pa-expiring-next-steps"
                  value={option.value}
                  checked={selectedValue === option.value}
                  onChange={() => setSelectedValue(option.value)}
                  variant="outlined"
                  disabled={handleTaskResponse.isPending}
                >
                  <RadioLabel>{option.label}</RadioLabel>
                </RadioOptionWrapper>
              ))}
            </RadioBlockGroup>
            <ButtonRow align={Alignment.SpaceBetween} fullWidth>
              <ButtonRow align={Alignment.Left}>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setShowDeclineModal(true)}
                  disabled={handleTaskResponse.isPending}
                >
                  Decline renewal
                </Button>
                <OtherRequestButton setShowNewFollowupTaskModal={setShowNewFollowupTaskModal} />
              </ButtonRow>
              <Button variant="primary" size="sm" onClick={handleSubmit} isLoading={handleTaskResponse.isPending}>
                Submit
              </Button>
            </ButtonRow>
          </ActionCard>
        )}
      </StructuredTaskViewWrapper>

      {showDeclineModal && (
        <DeclineModal
          closeModal={() => setShowDeclineModal(false)}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {showNewFollowupTaskModal && (
        <NewFollowupTaskModal
          closeModal={() => setShowNewFollowupTaskModal(false)}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default PaExpiringTaskView;
